import { coursePageResponsive, responsive } from '@common/constants'
import MsgTrans from '@components/common/i18n/message-trans'
import Loader from '@components/common/loader'
import { VideoTestimonialType } from '@lib/data/summer-coding-camp'
import { useEffect, useRef, useState } from 'react'
import 'react-multi-carousel/lib/styles.css'
import CommonCarousel from '@components/common/carousel'
import useResolution from '@hooks/useResolution'

type VideoTestimonialProps = {
  title: string | JSX.Element | React.ReactNode
  video_testimonial: VideoTestimonialType[]
  isCoursepage?: boolean
  subject?: 'math' | 'coding' | 'public speaking & communication'
}

// Extend the IframeHTMLAttributes interface
declare module 'react' {
  interface IframeHTMLAttributes<T> extends React.HTMLAttributes<T> {
    loading?: 'lazy' | 'eager' | 'auto'
  }
}

const VideoCard = ({ testimonial }: { testimonial: VideoTestimonialType }) => {
  const [isVisible, setIsVisible] = useState(false)
  const videoRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.disconnect() // Stop observing once it's visible
        }
      },
      { threshold: 0.1 } // Trigger when 10% of the video is in view
    )

    if (videoRef.current) {
      observer.observe(videoRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={videoRef} className="space-y-2 text-center">
      <div className="relative" style={{ height: '340px', width: '255px' }}>
        {isVisible ? (
          <iframe
            loading="lazy"
            src={`${testimonial.video}?autoplay=true&loop=true&muted=true&preload=false&responsive=true`}
            className="border-none absolute top-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          ></iframe>
        ) : (
          <div className="absolute top-0 h-full w-full bg-gray-300 flex items-center justify-center">
            <Loader />
          </div>
        )}
      </div>

      {testimonial.name && (
        <p className="font-600 text-2xl">
          <MsgTrans msg={testimonial.name} />
        </p>
      )}
      {testimonial.description && (
        <p className="text-sm">
          <MsgTrans msg={testimonial.description} />
        </p>
      )}
      {testimonial.type && (
        <p className="text-sm text-grey-800">
          <MsgTrans msg={testimonial.type} />
        </p>
      )}
    </div>
  )
}

const VideoTestimonial = ({
  title,
  video_testimonial,
  isCoursepage,
  subject = 'coding',
}: VideoTestimonialProps) => {
  const filteredTestimonials = video_testimonial.filter((e) =>
    Array.isArray(e.subject)
      ? e.subject.includes(subject)
      : e.subject === subject
  )
  const { isMobileScreen } = useResolution()

  return (
    <div
      className={` ${
        isCoursepage
          ? 'mx-auto container my-10 lg:px-3 py-5'
          : 'container mx-auto px-3 py-12 lg:py-16'
      } flex flex-col space-y-10 leading-tight text-grey`}
    >
      <h2
        className={
          isCoursepage
            ? 'text-2xl font-600 mb-4 leading-tight'
            : 'text-2xl lg:text-4xl font-600 mb-4 max-w-4xl mx-auto text-center'
        }
      >
        {title}
      </h2>
      <div
        className={
          isCoursepage &&
          'container max-w-xs sm:max-w-sm lg:max-w-xl xl:max-w-3xl mx-auto'
        }
        style={{
          maxWidth: isMobileScreen && isCoursepage && 'calc(100vw - 3.5rem)',
        }}
      >
        <CommonCarousel
          responsive={isCoursepage ? coursePageResponsive : responsive}
          autoPlaySpeed={undefined}
          partialVisible={true}
          itemClass="flex justify-center"
          hasArrows
        >
          {filteredTestimonials.map((testimonial, index) => (
            <div className="slider flex justify-center mb-10" key={index}>
              <VideoCard testimonial={testimonial} />
            </div>
          ))}
        </CommonCarousel>
      </div>
    </div>
  )
}

export default VideoTestimonial
